// src/pages/creator-panel/CallLogs/tabs/LogsTab.js
// Searchable / filterable list of lifecycle log entries for a call.
//
// Virtualization decision: we deliberately render a plain scroll-box (no
// `react-window`) today. Reason: the klonit recorder caps logs at ~30–80
// per call, and even noisy debug runs stay under 200. Below ~500 entries
// the user-perceptible cost of layout is zero, and avoiding `react-window`
// keeps the bundle smaller and the row markup trivially restyleable.
// Upgrade trigger: if a single call ever logs >500 entries (grep klonit
// logs for `[CallSession] finalize OK ... logs=NNN`), swap this `Box` for
// `<FixedSizeList itemSize={ROW_H} height={500}>` from `react-window`.
import React, { useMemo, useState } from 'react';
import {
    Box, TextField, InputAdornment, Select, MenuItem, Button, Typography, IconButton,
} from '@mui/material';
import { Search as SearchIcon, Download as DownloadIcon, FileDownload as ExportIcon } from '@mui/icons-material';
import JsonViewer from '../components/JsonViewer';
import { formatLogTime, categoryColor, levelColor } from '../utils/formatters';

const Pill = ({ label, bg, fg }) => (
    <Box sx={{
        display: 'inline-block', px: 1, py: 0.25, borderRadius: 0.75,
        bgcolor: bg, color: fg, fontSize: 11, fontWeight: 600,
        textTransform: 'capitalize', minWidth: 50, textAlign: 'center',
    }}>
        {label}
    </Box>
);

const LogRow = ({ row, expanded, onToggle }) => {
    const lvl = levelColor(row.level);
    const cat = categoryColor(row.category);
    const rawShort = row.raw ? JSON.stringify(row.raw).slice(0, 120) + (JSON.stringify(row.raw).length > 120 ? '…' : '') : '';
    return (
        <Box
            onClick={onToggle}
            sx={{
                display: 'grid',
                gridTemplateColumns: '180px 70px 110px 1fr',
                gap: 1.5,
                px: 1.5, py: 1,
                borderLeft: '3px solid',
                borderLeftColor: expanded ? 'primary.main' : 'transparent',
                bgcolor: expanded ? 'action.selected' : 'transparent',
                borderBottom: '1px solid', borderBottomColor: 'divider',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary', alignSelf: 'center' }}>
                {formatLogTime(row.occurred_at)}
            </Typography>
            <Box sx={{ alignSelf: 'center' }}><Pill label={row.level} bg={lvl.bg} fg={lvl.fg} /></Box>
            <Box sx={{ alignSelf: 'center' }}><Pill label={row.category} bg={cat.bg} fg={cat.fg} /></Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>{row.title}</Typography>
                <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.disabled', display: 'block' }} noWrap>
                    {rawShort}
                </Typography>
                {expanded && row.raw && (
                    <Box sx={{ mt: 1 }}>
                        <JsonViewer value={row.raw} />
                    </Box>
                )}
            </Box>
        </Box>
    );
};

const LogsTab = ({ logs }) => {
    const [search, setSearch] = useState('');
    const [level, setLevel] = useState('all');
    const [category, setCategory] = useState('all');
    const [expandedId, setExpandedId] = useState(null);

    const categories = useMemo(() => {
        const s = new Set();
        (logs || []).forEach(l => { if (l.category) s.add(l.category); });
        return Array.from(s).sort();
    }, [logs]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return (logs || []).filter(l => {
            if (level !== 'all' && (l.level || '').toLowerCase() !== level) return false;
            if (category !== 'all' && l.category !== category) return false;
            if (q) {
                const hay = `${l.title || ''} ${JSON.stringify(l.raw || {})}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });
    }, [logs, search, level, category]);

    const handleExport = () => {
        const rows = [['time', 'level', 'category', 'title', 'raw']];
        filtered.forEach(l => {
            rows.push([
                l.occurred_at || '',
                l.level || '',
                l.category || '',
                l.title || '',
                JSON.stringify(l.raw || {}),
            ]);
        });
        // Quote every cell, double inner quotes, collapse newlines so naïve
        // parsers don't choke on multi-line raw_json blobs.
        const csv = rows
            .map(r => r.map(cell => `"${String(cell).replace(/"/g, '""').replace(/\r?\n/g, ' ')}"`).join(','))
            .join('\r\n');
        // Prepend a UTF-8 BOM so Excel on Windows detects encoding correctly.
        const blob = new Blob(['﻿', csv], { type: 'text/csv;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `call-logs-${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        // Release the blob URL — without this each export leaks ~N KB until reload.
        URL.revokeObjectURL(url);
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                <TextField
                    size="small"
                    placeholder="Search logs"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                    }}
                    sx={{ minWidth: 240, flex: 1 }}
                />
                <Select size="small" value={level} onChange={(e) => setLevel(e.target.value)} displayEmpty sx={{ minWidth: 120 }}>
                    <MenuItem value="all">Level</MenuItem>
                    <MenuItem value="info">Info</MenuItem>
                    <MenuItem value="warn">Warn</MenuItem>
                    <MenuItem value="error">Error</MenuItem>
                </Select>
                <Select size="small" value={category} onChange={(e) => setCategory(e.target.value)} displayEmpty sx={{ minWidth: 140 }}>
                    <MenuItem value="all">Category</MenuItem>
                    {categories.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </Select>
                <Button size="small" variant="outlined" startIcon={<ExportIcon />} onClick={handleExport}>
                    Export
                </Button>
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto', fontWeight: 600 }}>
                    {filtered.length} {filtered.length === 1 ? 'log' : 'logs'}
                </Typography>
            </Box>

            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '180px 70px 110px 1fr',
                gap: 1.5,
                px: 1.5, py: 1,
                bgcolor: 'action.hover',
                color: 'text.secondary',
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: 0.8,
                textTransform: 'uppercase',
                borderTopLeftRadius: 4, borderTopRightRadius: 4,
            }}>
                <Box>Time</Box>
                <Box>Level</Box>
                <Box>Category</Box>
                <Box>Raw Data</Box>
            </Box>
            <Box sx={{
                maxHeight: 500, overflowY: 'auto',
                border: '1px solid', borderColor: 'divider',
                borderTop: 'none',
                borderBottomLeftRadius: 4, borderBottomRightRadius: 4,
            }}>
                {filtered.length === 0 ? (
                    <Typography color="text.disabled" sx={{ textAlign: 'center', py: 4, fontStyle: 'italic' }}>
                        No logs match the current filters.
                    </Typography>
                ) : (
                    filtered.map(row => (
                        <LogRow
                            key={row.id || row.sequence}
                            row={row}
                            expanded={expandedId === (row.id || row.sequence)}
                            onToggle={() => setExpandedId(prev => prev === (row.id || row.sequence) ? null : (row.id || row.sequence))}
                        />
                    ))
                )}
            </Box>
        </Box>
    );
};

export default LogsTab;
